import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { 
  WaterAdminRequest, 
  WaterAdminResponse,
  WaterAdminDetailRequest,
  WaterAdmin,
  SaveWaterAdminRequest,
  DeleteWaterAdminRequest,
  WaterAdminRow,
  DropdownColumn,
  WaterAdminCascadingFilterResponse,
  FilterColumn
} from './interfaces/water-admin-view.interfaces';
import { ErrorHandler } from '../utils/error-handler';
import { environment } from '../environments/environments';
import { WaterAdminApiItem, WaterAdminDetailsApiRequest, WaterAdminDetailsApiResponse } from './interfaces/water-admin-view.interfaces';

@Injectable({
  providedIn: 'root',
})
export class WaterAdminViewService {
  private baseUrl = environment.WebApiURL;
  private readonly waterAdminExportTabName = 'WaterAdmin';

  private normalizeFilterColumnsResponse(response: any): FilterColumn[] {
    const rawItems = Array.isArray(response)
      ? response
      : response?.waterAdminFilterDetails ||
        response?.dropDownItems ||
        response?.dropDownItem ||
        response?.columnFilters ||
        response?.data ||
        response?.result ||
        response?.value ||
        response?.$values ||
        [];

    if (!Array.isArray(rawItems)) {
      return [];
    }

    return rawItems
      .map((item: any) => ({
        columnName: String(item?.columnName ?? '').trim(),
        values: Array.isArray(item?.values)
          ? item.values
              .map((v: any) => String(v ?? '').trim())
              .filter((v: string) => v.length > 0)
          : [],
      }))
      .filter((item: FilterColumn) => item.columnName.length > 0);
  }

  private normalizeText(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  private normalizeDateForInput(value: string | null | undefined): string {
    const raw = this.normalizeText(value);
    if (!raw) {
      return '';
    }

    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }

    const slashDateMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashDateMatch) {
      const [, month, day, year] = slashDateMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }

    return '';
  }

  private mapApiItemToWaterAdmin(item: WaterAdminApiItem): WaterAdmin {
    return {
      region: this.normalizeText(item.region),
      feDevArea: this.normalizeText(item.feDevArea),
      intOutletPipelineName: this.normalizeText(item.waterFacilityName),
      elementType: this.normalizeText(item.facilityType),
      facilityType: this.normalizeText(item.facilityType), // Added missing property
      pipelineOutletDevArea: this.normalizeText(item.pipelineOutletDevArea),
      enersightAsset: this.normalizeText(item.enersightAsset),
      disposalOutletType: this.normalizeText(item.disposalOutletType),
      company: this.normalizeText(item.company),
      effectiveDate: this.normalizeDateForInput(item.effectiveDate),
      priority: item.priority,
      isActive: 'Y'
    };
  }

  private dropdownUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterAdmDropDown`;
  }

  private detailsUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterAdmDetails`;
  }

  private multiFilterUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterAdmMultiFilter`;
  }

  private saveUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/InsertUpdateWaterAdm`;
  }

  private deleteUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/RemoveWaterAdm`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  // private cascadingFilterUrl(): string {
  //   return `${this.baseUrl}/GetWaterAdminCascadingFilter`;
  // }

  constructor(private http: HttpClient) {}

  private parseDropdownResponse(response: FilterColumn[]): WaterAdminResponse {
    const findColumn = (name: string) =>
      response.find(item => (item.columnName || '').toLowerCase() === name.toLowerCase());

    const elementTypeItem = findColumn('facilityType');
    const regionItem = findColumn('region');
    const pipelineOutletDevAreaItem = findColumn('pipelineOutletDevArea');
    const developmentAreaItem = findColumn('DevelopmentArea');
    const subDevelopmentAreaItem = findColumn('SubDevelopmentArea');
    const feDevAreaItem = findColumn('feDevArea');
    const intOutletPipelineNameItem =
      findColumn('waterFacilityName') ||
      findColumn('FacilityName');
    const enersightAssetItem = findColumn('enersightAsset');
    const disposalOutletTypeItem = findColumn('disposalOutletType');
    const companyItem = findColumn('company');
    const priorityItem = findColumn('priority');

    const developmentAreas = developmentAreaItem?.values || [];
    const subDevelopmentAreas = subDevelopmentAreaItem?.values || [];
    const feDevAreas =
      feDevAreaItem?.values ||
      Array.from(new Set([...developmentAreas, ...subDevelopmentAreas]));

    return {
      regions: regionItem?.values || [],
      elementTypes: elementTypeItem?.values || [],
      pipelineOutletDevAreas: pipelineOutletDevAreaItem?.values || [],
      developmentAreas,
      subDevelopmentAreas,
      feDevAreas,
      intOutletPipelineNames: intOutletPipelineNameItem?.values || [],
      enersightAssets: enersightAssetItem?.values || [],
      disposalOutletTypes: disposalOutletTypeItem?.values || [],
      companies: companyItem?.values || [],
      priorities: (priorityItem?.values || [])
        .map(v => Number(v))
        .filter(v => Number.isFinite(v)),
    };
  }

  async getAllDropdownOptions(): Promise<WaterAdminResponse> {
    try {
      const requestBody: WaterAdminRequest = {
        waterFacilityName: null,
        region: null,
        feDevArea: null,
        pipelineOutletDevArea: null,
        company: null,
        enersightAsset: null,
        disposalOutletType: null,
        facilityType: null,
        priority: null
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.dropdownUrl(), requestBody)
      );
      const parsedResponse = this.parseDropdownResponse(
        this.normalizeFilterColumnsResponse(response)
      );
      return parsedResponse;
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterAdminViewService.getAllDropdownOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getWaterAdminData(filters?: Partial<WaterAdminDetailRequest>): Promise<WaterAdmin[]> {
    try {
      const payload: WaterAdminDetailsApiRequest = {
        waterFacilityName: filters?.intOutletPipelineName || null,
        region: filters?.region || null,
        feDevArea: filters?.feDevArea || null,
        pipelineOutletDevArea: filters?.pipelineOutletDevArea || null,
        company: filters?.company || null,
        enersightAsset: filters?.enersightAsset || null,
        disposalOutletType: filters?.disposalOutletType || null,
        facilityType: filters?.elementType || null,
        priority: filters?.priority ?? null,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        lastRowNumber: filters?.lastRowNumber ?? 0
      };

      const response = await firstValueFrom(
        this.http.post<WaterAdminDetailsApiResponse>(this.detailsUrl(), payload)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success) {
        return (response.waterAdminItems || []).map((item: WaterAdminApiItem) => this.mapApiItemToWaterAdmin(item));
      }

      console.warn('API returned non-success status:', parsed.statusCode, parsed.message);
      return [];
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterAdminViewService.getWaterAdminData');
      console.error('Error fetching water admin data:', error);
      return [];
    }
  }

  async getWaterAdminDataWithPagination(filters?: Partial<WaterAdminDetailRequest>): Promise<{
    data: WaterAdmin[];
    nextRowNumber: number | null;
  }> {
    try {
      const payload: WaterAdminDetailsApiRequest = {
        waterFacilityName: filters?.intOutletPipelineName || null,
        region: filters?.region || null,
        feDevArea: filters?.feDevArea || null,
        pipelineOutletDevArea: filters?.pipelineOutletDevArea || null,
        company: filters?.company || null,
        enersightAsset: filters?.enersightAsset || null,
        disposalOutletType: filters?.disposalOutletType || null,
        facilityType: filters?.elementType || null,
        priority: filters?.priority ?? null,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        lastRowNumber: filters?.lastRowNumber ?? 0
      };

      const response = await firstValueFrom(
        this.http.post<WaterAdminDetailsApiResponse>(this.detailsUrl(), payload)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success) {
        return {
          data: (response.waterAdminItems || []).map((item: WaterAdminApiItem) => this.mapApiItemToWaterAdmin(item)),
          nextRowNumber: response.nextRowNumber ?? null
        };
      }

      console.warn('API returned non-success status:', parsed.statusCode, parsed.message);
      return {
        data: [],
        nextRowNumber: null
      };
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterAdminViewService.getWaterAdminDataWithPagination');
      console.error('Error fetching water admin data:', error);
      return {
        data: [],
        nextRowNumber: null
      };
    }
  }

  async getCascadingFilterOptions(filters?: Partial<WaterAdminRequest>): Promise<WaterAdminCascadingFilterResponse> {
    try {
      const payload: WaterAdminRequest = {
        waterFacilityName: filters?.waterFacilityName || null,
        region: filters?.region || null,
        feDevArea: filters?.feDevArea || null,
        pipelineOutletDevArea: filters?.pipelineOutletDevArea || null,
        company: filters?.company || null,
        enersightAsset: filters?.enersightAsset || null,
        disposalOutletType: filters?.disposalOutletType || null,
        facilityType: filters?.facilityType || null,
        priority: filters?.priority || null
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.dropdownUrl(), payload)
      );

      return this.normalizeFilterColumnsResponse(response);
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterAdminViewService.getCascadingFilterOptions');
      console.error('Error fetching cascading filter options:', error);
      return [];
    }
  }

  async getMultiFilterOptions(filters?: Partial<WaterAdminDetailRequest>): Promise<WaterAdminResponse> {
    try {
      const payload: WaterAdminRequest = {
        waterFacilityName: filters?.intOutletPipelineName || null,
        region: filters?.region || null,
        feDevArea: filters?.feDevArea || null,
        pipelineOutletDevArea: filters?.pipelineOutletDevArea || null,
        company: filters?.company || null,
        enersightAsset: filters?.enersightAsset || null,
        disposalOutletType: filters?.disposalOutletType || null,
        facilityType: filters?.elementType || null,
        priority: filters?.priority ?? null
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.multiFilterUrl(), payload)
      );

      const dropdownItems: FilterColumn[] = this.normalizeFilterColumnsResponse(
        response
      );

      return this.parseDropdownResponse(dropdownItems);
    } catch (error) {
      ErrorHandler.handleError(error, 'WaterAdminViewService.getMultiFilterOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async exportAllWaterAdminData(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.waterAdminExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `water-admin-view${inferredExtension}`;
    const rawFileName = rawHeaderFileName || fallbackFileName;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return {
      blob,
      fileName,
    };
  }

  async saveWaterAdmin(row: WaterAdminRow, action: 'INSERT' | 'UPDATE', userName: string, originalRow?: WaterAdminRow): Promise<any> {
    try {
      const body: SaveWaterAdminRequest = {
        action,
        existingWaterFacilityName: action === 'UPDATE'
          ? (originalRow?.intOutletPipelineName || null)
          : null,
        newWaterFacilityName: row.intOutletPipelineName || '',
        facilityType: row.elementType || '',
        feDevArea: row.feDevArea || '',
        pipelineDevArea: row.pipelineOutletDevArea || '',
        company: row.company || '',
        oldEnersightAsset: action === 'UPDATE'
          ? (originalRow?.enersightAsset || null)
          : null,
        newEnersightAsset: row.enersightAsset || '',
        disposalOutletType: row.disposalOutletType || '',
        region: row.region || '',
        effectiveDate: row.effectiveDate || '',
        priority: row.priority ?? 0,
        userName: userName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.saveUrl(), body)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      console.error('Error saving water admin:', error);
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async saveMultipleWaterAdmin(rows: WaterAdminRow[], userName: string): Promise<any[]> {
    const results: any[] = [];
    
    for (const row of rows) {
      const action: 'INSERT' | 'UPDATE' = row.isNew ? 'INSERT' : 'UPDATE';
      const originalRow = row.original as WaterAdminRow;
      
      const result = await this.saveWaterAdmin(row, action, userName, originalRow);
      results.push(result);
    }

    return results;
  }

  async deleteWaterAdmin(row: WaterAdminRow, userName: string): Promise<any> {
    const requestBody: DeleteWaterAdminRequest = {
      waterFacilityName: row.intOutletPipelineName,
      facilityType: row.facilityType,
      feDevArea: row.feDevArea,
      pipelineDevArea: row.pipelineOutletDevArea,
      company: row.company,
      enersightAsset: row.enersightAsset,
      region: row.region,
      disposalOutletType: row.disposalOutletType,
      userName: userName
    };

    try {
      const response = await firstValueFrom(
        this.http.post<any>(this.deleteUrl(), requestBody)
      );

      if (typeof response === 'string') {
        return {
          statusCode: 200,
          statusMessage: response,
          error: false,
        };
      }

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      console.error('Error deleting water admin:', error);
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async deleteMultipleWaterAdmin(rows: WaterAdminRow[], userName: string): Promise<any[]> {
    const results: any[] = [];
    
    for (const row of rows) {
      if (row.isNew) {
        continue;
      }
      
      const result = await this.deleteWaterAdmin(row, userName);
      results.push(result);
    }

    return results;
  }

}

export type { 
  WaterAdminRequest,
  WaterAdminResponse,
  WaterAdminDetailRequest,
  WaterAdmin,
  SaveWaterAdminRequest,
  DeleteWaterAdminRequest,
  WaterAdminRow,
  DropdownColumn,
  WaterAdminCascadingFilterResponse,
  FilterColumn
} from './interfaces/water-admin-view.interfaces';
